import { motion } from "framer-motion";
import { useAuth } from "../../../context/AuthContext";
import Pagination from "../../ui/pagination";

import {
    PlusIcon,
    PencilSquareIcon,
    ArrowUpTrayIcon,
    ArrowDownTrayIcon,
    XMarkIcon,
    UserPlusIcon,
} from "@heroicons/react/24/outline";

import {
    Play,
    Clock,
    FileQuestion,
    SendIcon,
    CheckCircle2,
    XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import AssignLMSModuleModal from "./AssignLMSModuleModal";
import { assignModuleApi, exportLMSDataApi } from "../../../services/axios/AuthApis/AiLMSApis";
import toast from "../../../utils/notify";
import ApiLoader from "../../ui/ApiLoader";
import AiLmsFormModal from "../AiLmsFormModal";
import BulkUploadModal from "../BulkUploadModal";

export const TrainingCatalog = ({ loading, modules, setShowLmsModal, setShowUploadModal, setModuleLoading, setActiveTab, setWatchVideoEnabled, selectedModuleId, setSelectedModuleId, pagination, setPagination, fetchModules, setEditingModule }) => {
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedModule, setSelectedModule] = useState(null);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [checkedModuleIds, setCheckedModuleIds] = useState([]);
    const { user, token } = useAuth();

    const isAssignRole = user?.role === "customer_admin" || user?.role === "security_analyst";

    const toggleCheck = (moduleId) => {
        setCheckedModuleIds((prev) =>
            prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
        );
    };

    const handleAssignComplete = async ({ module_ids, employees, due_date }) => {
        try {
            await Promise.all(
                module_ids.map((id) => assignModuleApi({ customer_lms_master_id: id, employees, due_date }))
            );
            toast.success(
                module_ids.length > 1
                    ? `${module_ids.length} modules assigned successfully`
                    : 'Module assigned successfully'
            );
            setShowAssignModal(false);
            setCheckedModuleIds([]);
        } catch {
            toast.error('Failed to assign module(s)');
        }
    };

    const handleAssign = (module) => {
        setSelectedModule(module);
        setSelectedModuleId(module.id);
        setCheckedModuleIds([module.id]);
        setShowAssignModal(true);
    };

    const handleAssignSelected = () => {
        setShowAssignModal(true);
    };
    
    const handleEditModule = (module) => {
        if (setEditingModule) {
            setEditingModule(module);
            setShowLmsModal(true);
        }
    };

    const handleBulkUploadSuccess = () => {
        fetchModules();
    };

    const handleExportLMS = async () => {
        try {
            setIsExporting(true);
            const data = await exportLMSDataApi();
            
            // Create JSON file
            const element = document.createElement('a');
            const file = new Blob([JSON.stringify(data, null, 2)], {
                type: 'application/json'
            });
            element.href = URL.createObjectURL(file);
            element.download = `LMS_Master_Final_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            
            toast.success('LMS data exported successfully!');
        } catch (error) {
            console.error('Export error:', error);
            toast.error(error.response?.data?.message || 'Failed to export LMS data');
        } finally {
            setIsExporting(false);
        }
    };
    
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
             {user?.role === "customer_admin" && (
                <div className="flex justify-end mb-2">
                    <span className="text-xs md:text-sm text-gray-400 italic">Note: Select an LMS module to assign to employees.</span>
                </div>
            )}
        {/* Floating multi-select bar */}
        {isAssignRole && checkedModuleIds.length > 0 && (
            <div className="sticky top-0 z-30 flex items-center justify-between gap-3 mb-4 px-4 py-3 bg-blue-600 text-white rounded-xl shadow-lg">
                <span className="text-sm font-medium">
                    {checkedModuleIds.length} module{checkedModuleIds.length !== 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setCheckedModuleIds(modules.map((m) => m.id))}
                        className="text-xs px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition"
                    >
                        Select all
                    </button>
                    <button
                        onClick={handleAssignSelected}
                        className="flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 bg-white text-blue-700 rounded-lg hover:bg-blue-50 transition"
                    >
                        <UserPlusIcon className="w-4 h-4" /> Assign Selected
                    </button>
                    <button
                        onClick={() => setCheckedModuleIds([])}
                        className="p-1 rounded hover:bg-white/20 transition"
                        title="Clear selection"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>
        )}

        <AssignLMSModuleModal
            isOpen={showAssignModal}
            onClose={() => { setShowAssignModal(false); setCheckedModuleIds([]); }}
            moduleIds={checkedModuleIds}
            onAssign={handleAssignComplete}
        />

            <div className="flex justify-end mt-6 mb-6 gap-3">
                            {(user?.role === "platform_admin" || user?.role === "admin") && (
                <>
                <button
                    onClick={handleExportLMS}
                    disabled={isExporting}
                    className="inline-flex items-center justify-center
                            min-w-[180px] px-6 py-2
                            bg-green-600 border border-transparent
                            rounded-lg text-sm font-medium text-white
                            hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed
                            transition-all duration-200
                            shadow-sm hover:shadow-md"
                    title="Download all LMS data as JSON"
                >
                    {isExporting ? (
                        <>
                            <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                            Exporting...
                        </>
                    ) : (
                        <>
                            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                            Export
                        </>
                    )}
                </button>
                <button
                    onClick={() => {
                        if (setEditingModule) setEditingModule(null);
                        setShowBulkUploadModal(true);
                    }}
                    className="inline-flex items-center justify-center
                            min-w-[180px] px-6 py-2
                            bg-blue-800 border border-transparent
                            rounded-lg text-sm font-medium text-white
                            hover:bg-violet-800 transition-all duration-200
                            shadow-sm hover:shadow-md"
                    title="Upload multiple LMS modules from JSON file"
                >
                    <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                    Bulk Upload
                </button>
                <button
                    onClick={() => {
                        if (setEditingModule) setEditingModule(null);
                        setShowUploadModal(true);
                    }}
                    className="inline-flex items-center justify-center
                            min-w-[160px] px-6 py-2
                            bg-blue-600 border border-transparent
                            rounded-lg text-sm font-medium text-white
                            hover:bg-blue-700 transition-all duration-200
                            shadow-sm hover:shadow-md"
                >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    New LMS
                </button>
                </>
                 )}
            </div>

            <BulkUploadModal
                isOpen={showBulkUploadModal}
                onClose={() => setShowBulkUploadModal(false)}
                onUploadSuccess={handleBulkUploadSuccess}
            />
       
            {loading && modules.length > 0 && <ApiLoader variant="bar" />}
            {loading && modules.length === 0 ? (
                <ApiLoader label="Loading module content..." className="py-20" />
            ) : modules.length === 0 ? (
                <div className="text-center py-20 text-gray-500">
                    No training modules available.
                </div>
            ) : (
                <div aria-busy={loading} className={`transition-opacity ${loading ? 'pointer-events-none opacity-50' : ''}`}>
                    <div className="grid sm:grid-cols-2 gap-4">

                        {modules.map((module, index) => {
                            const Icon = module.icon;
                            const displayQuizCount = module.quizQuestions;
                            return (
                                <div
                                    key={module.id}
                                    className={`rounded-2xl border bg-white p-6 transition hover:shadow-lg relative ${
                                        checkedModuleIds.includes(module.id)
                                            ? 'border-blue-400 ring-2 ring-blue-200'
                                            : 'border-blue-100 hover:border-blue-200'
                                    }`}
                                >
                                    {/* Multi-select checkbox - top-left for assign roles */}
                                    {isAssignRole && (
                                        <input
                                            type="checkbox"
                                            checked={checkedModuleIds.includes(module.id)}
                                            onChange={() => toggleCheck(module.id)}
                                            className="absolute top-4 left-4 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                            title="Select for bulk assign"
                                        />
                                    )}

                                    {/* Edit Button - Top Right */}
                                    {(user?.role === "platform_admin" || user?.role === "admin") && (
                                        <button
                                            onClick={() => handleEditModule(module)}
                                            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="Edit Module"
                                        >
                                            <PencilSquareIcon className="w-5 h-5" />
                                        </button>
                                    )}
                                    
                                    {/* Header */}
                                    <div className={`flex items-start gap-3 mb-3 ${isAssignRole ? 'pl-6' : ''}`}>
                                        <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-blue-50 flex-shrink-0 mt-0.5">
                                            <Icon className="h-6 w-6 text-blue-600" />
                                        </div>
                                        <h3 className="text-base font-semibold text-gray-900 line-clamp-2 flex-1">
                                            {module.title}
                                        </h3>
                                    </div>

                                    {/* Description */}
                                    {/* <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                                        {module.description}
                                    </p> */}

                                    {/* Meta */}
                                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                                        <div className="flex items-center gap-1">
                                            <Clock className="h-4 w-4" />
                                            {module.duration}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <FileQuestion className="h-4 w-4" />
                                            {displayQuizCount} Questions
                                        </div>
                                    </div>

                                    {/* Quiz result badge — end user only */}
                                    {module.video_seen && module.quiz_result && (
                                        <div className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg mb-4 w-fit ${
                                            module.quiz_result.status
                                                ? "bg-green-50 text-green-700 border border-green-200"
                                                : "bg-red-50 text-red-700 border border-red-200"
                                        }`}>
                                            {module.quiz_result.status
                                                ? <CheckCircle2 className="h-3.5 w-3.5" />
                                                : <XCircle className="h-3.5 w-3.5" />
                                            }
                                            Quiz Score: {module.quiz_result.score}% &nbsp;·&nbsp;
                                            {module.quiz_result.status ? "Passed" : "Failed"}
                                        </div>
                                    )}

                                    {/* ISO Tags */}
                                    {/* <div className="flex gap-2 mb-5">
                            {module.isoAlignment.map((iso) => (
                              <span
                                key={iso}
                                className="rounded-md bg-gray-100 px-2.5 py-1 text-xs text-gray-600"
                              >
                                {iso}
                              </span>
                            ))}
                          </div> */}
                                    {/* CTA Button */}


                                    {(user?.role === "platform_admin" ||user?.role === "admin" || user?.role === "end_user") && !module.quiz_result?.status && (        
                                    <button
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            setWatchVideoEnabled(true);
                                            setModuleLoading(true);
                                            setSelectedModuleId(module.id);
                                            setActiveTab("watch_video");
                                        }}
                                        className="flex w-full items-center justify-center gap-2
                                    rounded-lg bg-blue-600 py-3 text-sm font-semibold
                                    text-white hover:bg-blue-700 transition"
                                    >
                                        <Play className="h-4 w-4" />
                                        Start Module
                                    </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {pagination.total_count > 0 && (
                        <Pagination
                            currentPage={pagination.page}
                            totalPages={pagination.total_pages}
                            totalCount={pagination.total_count}
                            pageSize={pagination.page_size}
                            onPageChange={(newPage, pageSize) => {
                                setPagination((p) => ({ ...p, page: newPage, page_size: pageSize }));
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            loading={loading}
                        />
                    )}
                </div>

            )}
        </motion.div>
    )
}